import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrokerDealFlowShell } from "../../components/BrokerDealFlowShell";
import { BrokerOnly } from "../../components/BrokerOnly";
import { dealSubmissionApi } from "../../api/dealSubmission";
import { PortalApiError } from "../../api/client";
import { usePortalAuth } from "../../PortalAuthContext";

// Deal Submission spec §11 — wizard entry. The route is
// hit when the broker clicks "Start a new deal submission". Certified
// brokers get a fresh draft and are redirected into the runner; everyone
// else sees the "certification required" card and no draft is created.
//
// WS1 — DELIBERATE CLIENT/SERVER ASYMMETRY. Do NOT "fix" the inconsistency
// by tightening the server to match this card. What you see below is a UX
// NUDGE: it blocks a strict SUPERSET (everything that is not Certified) to
// push a broker mid-pipeline toward finishing onboarding. The server-side
// security boundary — PortalBrokerController.ResolveBrokerIdForWriteAsync,
// which every deal-submission WRITE resolves through — blocks only the
// hard-negative "Archive" subset of BrokerStatus (Inactive / Suspended /
// Disqualified / Declined), so a Prospect / Qualified / Onboarding broker can
// still be walked through a submission by a liaison. PR #451 (2026-05-30,
// "fully-manual status lifecycle") deliberately removed the old blanket
// "must be Certified" gate from that controller as lifecycle-not-security —
// do NOT re-add it there.

export function DealSubmissionWizardEntry() {
  return (
    <BrokerOnly>
      <EntryBody />
    </BrokerOnly>
  );
}

function EntryBody() {
  const navigate = useNavigate();
  const auth = usePortalAuth();
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  async function handleStart() {
    setStarting(true);
    setError(null);
    try {
      const draft = await dealSubmissionApi.createDraft({});
      navigate(`/portal/broker/deals/submissions/${draft.id}`, { replace: true });
    } catch (err) {
      const msg =
        err instanceof PortalApiError ? err.message
          : err instanceof Error ? err.message
          : "Couldn't start a new deal submission.";
      setError(msg);
      setStarting(false);
    }
  }

  // fix-broker-portal — gate on the certification status from /me (already
  // loaded) rather than calling the broker dashboard endpoint, which 403s
  // ("Broker session required.") on a freshly self-healed broker session.
  if (auth.loading || !auth.me) {
    return (
      <BrokerDealFlowShell pageTitle="Submit a deal">
        <p className="text-small text-gray-mid">Loading…</p>
      </BrokerDealFlowShell>
    );
  }

  if (auth.me.isCertified !== true) {
    return (
      <BrokerDealFlowShell pageTitle="Submit a deal">
        <h1 className="text-h2 font-display text-onyx m-0">Submit a deal</h1>
        <div
          className="mt-space-4 p-space-4 border border-border-light rounded-md bg-white-warm/40"
          data-testid="new-deal-locked"
        >
          <p className="text-small text-onyx m-0">Certification required.</p>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            Finish the onboarding course to unlock deal submission.{" "}
            <Link to="/portal/broker/course" className="underline">Continue onboarding</Link>
          </p>
        </div>
      </BrokerDealFlowShell>
    );
  }

  return (
    <BrokerDealFlowShell pageTitle="Submit a deal">
      <h1 className="text-h2 font-display text-onyx m-0">Submit a new deal</h1>
      <p className="text-small text-gray-mid mt-space-2">
        We'll walk you through the deal in sections. You can save and continue at any time —
        a draft is created the moment you click below.
      </p>

      {error && (
        <p className="text-small text-status-error m-0 mt-space-3" role="alert">{error}</p>
      )}

      <div className="mt-space-5 flex items-center gap-space-3">
        <button
          type="button"
          onClick={() => void handleStart()}
          disabled={starting}
          data-testid="new-deal-start"
          className="px-space-4 py-space-2 rounded-md bg-onyx text-white-pure text-small disabled:opacity-60"
        >
          {starting ? "Starting…" : "Start a new deal submission"}
        </button>
        <Link to="/portal/broker?tab=deals" className="text-small text-onyx underline">
          Back to my deals
        </Link>
      </div>
    </BrokerDealFlowShell>
  );
}
